import type { LoginRequest, LoginResponse } from "@developerz.ai/ggcoin-sdk";
import type { PersistedSession } from "../sdk/storage.ts";

/** The slice of router location state the login page reads. */
export interface LoginLocationState {
  from?: string;
}

/**
 * Where to send the user after a successful login. The guard stashes the
 * originally requested path in `location.state.from`; fall back to the root so
 * a direct visit to `/login` still lands somewhere useful.
 */
export function resolveLoginRedirect(state: LoginLocationState | null | undefined): string {
  const from = state?.from;
  return from && from.length > 0 ? from : "/";
}

/** Project the SDK login response onto the persisted-session shape. */
export function toPersistedSession(res: LoginResponse): PersistedSession {
  return {
    jwt: res.jwt,
    accountId: res.accountId,
    sessionId: res.sessionId,
    expiresAt: res.expiresAt,
  };
}

/** Normalize a raw TOTP input to at most six digits (strips everything else). */
export function sanitizeTotp(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 6);
}

/** The client surface the login flow drives. */
export interface LoginClient {
  login(input: LoginRequest): Promise<LoginResponse>;
}

/** Side effects the login flow performs on success. */
export interface LoginEffects {
  setSession(next: PersistedSession): void;
  navigate(to: string): void;
}

/**
 * Orchestrate a single login attempt: call the client, persist the session,
 * and navigate to the resolved destination. Extracted from the component so the
 * happy path and the failure path are unit-testable without a DOM.
 *
 * Throws whatever the client throws (the caller surfaces it via the error
 * banner); on success it returns the destination it navigated to.
 */
export async function performLogin(
  client: LoginClient,
  input: LoginRequest,
  state: LoginLocationState | null | undefined,
  effects: LoginEffects,
): Promise<string> {
  const res = await client.login(input);
  effects.setSession(toPersistedSession(res));
  const to = resolveLoginRedirect(state);
  effects.navigate(to);
  return to;
}
